import React, { useEffect, useMemo, useState } from "react";
import { Calendar, ChevronDown } from "lucide-react";

/**
 * Date-range picker with brutalist aesthetic.
 * - Preset chips: today, 7d, 30d, this/last month, YTD, all time
 * - Custom from/to inputs
 *
 * Emits `onChange({ from, to, presetKey })` whenever the range changes.
 * `from`/`to` are YYYY-MM-DD strings (or "" for unbounded).
 */
const PRESETS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "ytd", label: "Year to date" },
  { key: "all", label: "All time" },
  { key: "custom", label: "Custom" },
];

function isoDay(d) {
  return d.toISOString().slice(0, 10);
}

export function rangeForPreset(key) {
  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  if (key === "today") {
    return { from: isoDay(today), to: isoDay(today) };
  }
  if (key === "7d") {
    const start = new Date(today); start.setUTCDate(start.getUTCDate() - 6);
    return { from: isoDay(start), to: isoDay(today) };
  }
  if (key === "30d") {
    const start = new Date(today); start.setUTCDate(start.getUTCDate() - 29);
    return { from: isoDay(start), to: isoDay(today) };
  }
  if (key === "this_month") {
    const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
    return { from: isoDay(start), to: isoDay(today) };
  }
  if (key === "last_month") {
    const lm = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
    const lmEnd = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0));
    return { from: isoDay(lm), to: isoDay(lmEnd) };
  }
  if (key === "ytd") {
    const start = new Date(Date.UTC(today.getUTCFullYear(), 0, 1));
    return { from: isoDay(start), to: isoDay(today) };
  }
  return { from: "", to: "" };
}

export default function DateRangePicker({ value, onChange, className = "", testIdPrefix = "drp" }) {
  const [open, setOpen] = useState(false);
  const initial = value || { from: "", to: "", presetKey: "30d" };
  const [presetKey, setPresetKey] = useState(initial.presetKey || "30d");
  const [from, setFrom] = useState(initial.from || "");
  const [to, setTo] = useState(initial.to || "");

  // Initialize default range on first mount
  useEffect(() => {
    if (!initial.from && !initial.to && initial.presetKey !== "all") {
      const r = rangeForPreset(presetKey);
      setFrom(r.from);
      setTo(r.to);
      onChange?.({ from: r.from, to: r.to, presetKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choosePreset = (key) => {
    setPresetKey(key);
    if (key === "custom") {
      setOpen(true);
      return;
    }
    const r = rangeForPreset(key);
    setFrom(r.from);
    setTo(r.to);
    onChange?.({ from: r.from, to: r.to, presetKey: key });
    setOpen(false);
  };

  const applyCustom = () => {
    onChange?.({ from, to, presetKey: "custom" });
    setOpen(false);
  };

  const label = useMemo(() => {
    if (presetKey === "all") return "All time";
    if (presetKey === "custom") {
      if (!from && !to) return "Custom";
      return `${from || "…"} → ${to || "…"}`;
    }
    return PRESETS.find((p) => p.key === presetKey)?.label || "Last 30 days";
  }, [presetKey, from, to]);

  return (
    <div className={`relative inline-block ${className}`} data-testid={`${testIdPrefix}-root`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="brutal-btn brutal-btn-secondary !py-2 !px-3 inline-flex items-center gap-2"
        data-testid={`${testIdPrefix}-trigger`}
      >
        <Calendar size={14} />
        <span className="font-mono text-xs uppercase tracking-widest">{label}</span>
        <ChevronDown size={12} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 sm:right-0 sm:left-auto mt-2 z-40 brutal-card !p-3 bg-white min-w-[260px] max-w-[calc(100vw-32px)]" data-testid={`${testIdPrefix}-menu`}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A] mb-2">Quick picks</div>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => choosePreset(p.key)}
                  className={`brutal-badge !py-1 !px-2 cursor-pointer ${presetKey === p.key ? "!bg-[#C2410C] !text-white" : ""}`}
                  data-testid={`${testIdPrefix}-preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-black/10">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A] mb-1">Custom range</div>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={from}
                  onChange={(e) => { setFrom(e.target.value); setPresetKey("custom"); }}
                  className="brutal-input !py-1 !px-2 text-xs"
                  data-testid={`${testIdPrefix}-from`}
                />
                <span className="font-mono text-xs">→</span>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => { setTo(e.target.value); setPresetKey("custom"); }}
                  className="brutal-input !py-1 !px-2 text-xs"
                  data-testid={`${testIdPrefix}-to`}
                />
              </div>
              <button
                onClick={applyCustom}
                className="brutal-btn !py-2 mt-2 w-full text-xs"
                data-testid={`${testIdPrefix}-apply`}
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
